import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Search, Shield, ListChecks, FileCheck } from "lucide-react";
import ArqNavigation from "@/components/arqera/ArqNavigation";
import ArqFooter from "@/components/arqera/ArqFooter";
import { openBookingModal } from "@/components/arqera/ArqBookingModal";
import { SEO } from "@/components/SEO";

const reviewRows = [
  { id: "TEN-ISO", sev: "AT RISK", sevColor: "#D97706", title: "Tenant Isolation", meta: "Cross-tenant access path observed" },
  { id: "LLM01", sev: "FOUND", sevColor: "#C8402A", title: "Prompt Injection Path", meta: "OWASP LLM01 · Tool-use exfiltration" },
  { id: "PRV-LOG", sev: "REVIEW", sevColor: "hsl(var(--arq-teal))", title: "Sensitive Data Logging", meta: "PII written to model trace logs" },
  { id: "LNCH", sev: "3 CRITICAL", sevColor: "#C8402A", title: "Launch Readiness", meta: "3 critical gaps before customer rollout" },
];

const riskCards = [
  {
    title: "Customer Data Exposure",
    body: "AI features can retrieve, summarize, or disclose sensitive data if access controls, retrieval boundaries, and tenant isolation are not validated.",
  },
  {
    title: "Prompt Injection and Workflow Abuse",
    body: "Users or attackers may manipulate prompts, tool calls, retrieval paths, or agentic workflows to trigger unintended actions or expose restricted information.",
  },
  {
    title: "Enterprise Deal Friction",
    body: "Security reviews, compliance questionnaires, auditors, and enterprise buyers increasingly ask how AI features are governed, tested, remediated, and validated.",
  },
];

const bestFor = [
  "B2B SaaS teams launching AI features",
  "Healthcare, fintech, legaltech, logistics, HR tech, and insurance platforms",
  "Teams preparing for enterprise security review",
  "Companies using chatbots, copilots, RAG search, AI agents, or AI workflow automation",
  "Products where AI touches customer, employee, financial, health, legal, operational, or regulated data",
];

const scopeItems = [
  "Customer data leakage",
  "Tenant isolation and authorization flaws",
  "Prompt injection and jailbreak paths",
  "Unsafe tool or workflow access",
  "RAG and document exposure",
  "Sensitive data in prompts, responses, and logs",
  "Privacy and consent gaps",
  "AI vendor and third-party risk",
  "Compliance and evidence readiness",
  "Launch-readiness risks for enterprise review",
];

const deliverables = [
  {
    title: "Executive Risk Summary",
    body: "A business-readable summary of the most important risks, likely impact, and recommended next decisions.",
  },
  {
    title: "Technical Findings Report",
    body: "Documented findings with severity, affected areas, evidence, and remediation guidance for engineering and security teams.",
  },
  {
    title: "Prioritized Remediation Roadmap",
    body: "A practical fix path organized by urgency, business impact, implementation effort, and validation requirements.",
  },
  {
    title: "Validation and Evidence Package",
    body: "Evidence-ready documentation suitable for leadership, auditors, enterprise buyers, security review, compliance support, or board reporting.",
  },
];

const steps = [
  {
    n: "01",
    icon: Search,
    title: "Scope the Feature",
    body: "We review the AI feature, data flows, users, permissions, vendors, compliance obligations, and rollout timeline.",
  },
  {
    n: "02",
    icon: Shield,
    title: "Test the Risk Surface",
    body: "We assess data exposure, authorization, prompt injection, retrieval boundaries, logging, unsafe actions, and application-layer AI risks.",
  },
  {
    n: "03",
    icon: ListChecks,
    title: "Prioritize the Fixes",
    body: "Findings are converted into a remediation roadmap with clear owners, severity, implementation guidance, and validation criteria.",
  },
  {
    n: "04",
    icon: FileCheck,
    title: "Validate and Prove",
    body: "Critical fixes are validated and packaged into evidence-ready documentation for leadership, security, compliance, auditors, or enterprise review.",
  },
];

const goodFit = [
  "Launching a customer-facing AI feature",
  "Adding AI to an existing SaaS platform",
  "Preparing for enterprise security review",
  "Handling regulated or sensitive data",
  "Using RAG, agents, copilots, chatbots, or workflow automation",
  "Need remediation guidance, not just findings",
];

const notIdeal = [
  "Pure research prototypes with no production path",
  "Teams looking only for generic AI strategy",
  "Organizations unwilling to remediate critical findings",
  "Projects with no defined AI feature, data flow, or business owner",
];

const expansion = [
  {
    title: "Remediation Sprint",
    body: "Focused support to fix critical and high-priority findings across access control, retrieval boundaries, logging, privacy, configuration, and workflow risk.",
  },
  {
    title: "Validation Package",
    body: "Retesting and evidence assembly to prove the fixes are working and prepare documentation for internal or external review.",
  },
  {
    title: "Ongoing AI Assurance",
    body: "Monthly or quarterly review of new AI features, remediation status, regulatory changes, and evidence readiness.",
  },
];

const faqs = [
  {
    q: "Is this only for pre-launch AI features?",
    a: "No. The review works for both pre-launch and already-deployed AI features. Pre-launch reviews reduce rollout risk. Post-launch reviews identify what is already exposed and what should be remediated first.",
  },
  {
    q: "Is this the same as AI red teaming?",
    a: "No. AI red teaming is one component of broader AI security. This review focuses on the full application-layer risk surface around the AI feature: data access, authorization, prompts, retrieval, tools, logging, privacy, compliance, and remediation readiness.",
  },
  {
    q: "Do you only work with regulated industries?",
    a: "No. ArqeraAI is strongest where AI risk has business consequences: regulated data, enterprise buyers, compliance obligations, customer trust, or sensitive workflows. That includes regulated industries and B2B software companies selling into regulated customers.",
  },
  {
    q: "What happens after the review?",
    a: "Most teams use the review to prioritize remediation. ArqeraAI can support remediation, validation, evidence preparation, and ongoing assurance after the initial review.",
  },
  {
    q: "How long does it take?",
    a: "The focused review is designed around a 10-business-day timeline, depending on access, scope, and complexity. Larger environments may require a broader assessment.",
  },
  {
    q: "Who should be involved?",
    a: "The best review usually includes a technical owner, a security or compliance owner, and a product or business owner who understands the feature's rollout path and customer impact.",
  },

  {
    q: "What do we need to provide?",
    a: "Typical inputs include an overview of the AI feature, architecture or data flow notes, test access where appropriate, vendor details, compliance obligations, and any known security or customer-review concerns.",
  },
];

function scrollTo(id: string) {
  document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
}

export default function AIFeatureSecurityReview() {
  const [activeRow, setActiveRow] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => setActiveRow((i) => (i + 1) % reviewRows.length), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-arq-bg text-arq-text font-body">
      <SEO
        title="AI Feature Security Review: ArqeraAI"
        description="A focused review for teams launching chatbots, copilots, RAG search, AI agents, or AI workflow automation. Find, fix, and validate risks around data exposure, authorization, prompt injection, logging, privacy, and compliance evidence."
        ogTitle="AI Feature Security Review: ArqeraAI"
        ogDescription="Find, fix, and validate AI feature risks before they expose customer data, break compliance, or stall enterprise deals."
        canonical="https://arqeraai.com/ai-feature-security-review"
      />
      <ArqNavigation />

      <main>
        {/* HERO */}
        <section className="arq-radial-tl relative min-h-[88vh] flex items-center pt-32 pb-20 px-6 overflow-hidden">
          <div
            aria-hidden
            className="hidden md:block absolute right-0 top-0 bottom-0 w-[45%] opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(hsl(var(--arq-blue)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--arq-blue)) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
              maskImage: "radial-gradient(ellipse at 70% 50%, black 30%, transparent 75%)",
            }}
          />
          <div className="max-w-[1200px] mx-auto w-full relative z-10">
            <div className="grid md:grid-cols-[55%_40%] md:gap-[5%] items-center">
              <div>
                <div className="arq-label mb-6">AI Feature Security Review</div>
                <h1
                  className="font-display font-bold text-arq-text leading-[1.1] tracking-[-0.02em] mb-6"
                  style={{ fontSize: "clamp(40px, 6.5vw, 64px)" }}
                >
                  Ship AI Features <span className="text-arq-blue">Safely.</span>
                </h1>
                <p
                  className="text-arq-muted mb-4 max-w-[600px]"
                  style={{ fontSize: "clamp(17px, 1.6vw, 20px)", lineHeight: 1.6 }}
                >
                  Before your chatbot, copilot, RAG search, AI agent, or AI workflow reaches
                  customers, ArqeraAI helps find, fix, and validate the risks that could expose
                  data, break compliance, or stall enterprise deals.
                </p>
                <p className="text-arq-tertiary text-[14.5px] leading-[1.65] mb-8 max-w-[600px]">
                  Built for software and regulated teams that need practical security assurance
                  without slowing product momentum.
                </p>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
                  <button
                    onClick={() => openBookingModal()}
                    className="bg-arq-blue text-white font-semibold text-[15px] px-8 py-4 rounded-md hover:bg-arq-blue-bright hover:shadow-[0_0_24px_rgba(26,111,212,0.35)] transition-all duration-150 tracking-[0.01em]"
                  >
                    Book an AI Feature Security Review
                  </button>
                  <button
                    onClick={() => scrollTo("#scope")}
                    className="bg-transparent text-arq-text font-medium text-[15px] px-2 py-4 hover:underline underline-offset-4 transition-all duration-150"
                  >
                    See What We Review
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-[13px] text-arq-tertiary">
                  <span>10-business-day focused review</span>
                  <span>·</span>
                  <span>Executive and technical findings</span>
                  <span>·</span>
                  <span>Remediation roadmap</span>
                  <span>·</span>
                  <span>Evidence-ready deliverables</span>
                </div>

              </div>

              {/* Right card */}
              <div className="hidden md:block">
                <div className="relative bg-arq-surface1 border border-arq-border rounded-xl p-6 shadow-[0_8px_40px_rgba(0,0,0,0.4)] overflow-hidden">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-arq-blue/60 to-transparent motion-safe:animate-[arq-scan_4.5s_linear_infinite]"
                    style={{ top: 0 }}
                  />
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] uppercase text-arq-muted">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inset-0 rounded-full bg-arq-teal motion-safe:animate-ping opacity-75" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-arq-teal" />
                      </span>
                      AI Feature Security Review
                    </div>
                    <div className="font-mono text-[10px] text-arq-tertiary tracking-[0.08em]">REV-████</div>
                  </div>
                  <div className="space-y-2.5">
                    {reviewRows.map((row, i) => {
                      const isActive = activeRow === i;
                      return (
                        <div
                          key={row.id}
                          className={`p-3 rounded-md bg-arq-bg/60 border transition-all duration-500 ${
                            isActive
                              ? "border-arq-blue/60 shadow-[0_0_0_1px_hsl(var(--arq-blue)/0.25),0_0_24px_-4px_hsl(var(--arq-blue)/0.45)] translate-x-[2px]"
                              : "border-arq-border"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className="font-mono text-[10px] tracking-[0.08em] px-1.5 py-0.5 rounded"
                              style={{
                                color: row.sevColor,
                                background: `${row.sevColor}1A`,
                                border: `1px solid ${row.sevColor}40`,
                              }}
                            >
                              {row.sev}
                            </span>
                            <span className="font-mono text-[10px] text-arq-tertiary tracking-[0.06em]">
                              {row.id}
                            </span>
                          </div>
                          <div className="text-arq-text text-[13.5px] leading-tight mb-1">
                            {row.title}
                          </div>
                          <div className="font-mono text-[10.5px] text-arq-muted tracking-[0.03em]">
                            {row.meta}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-3 border-t border-arq-border font-mono text-[10.5px] text-arq-tertiary tracking-[0.05em] flex items-center justify-between">
                    <span>ArqeraAI Review · Executive + technical remediation roadmap</span>
                    <span className="text-arq-teal motion-safe:animate-pulse">●</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM */}
        <section className="bg-arq-bg py-20 md:py-[120px] px-6 border-t border-arq-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="max-w-[820px] mb-14">
              <div className="arq-label mb-5">The Launch Risk</div>
              <h2
                className="font-display font-semibold text-arq-text mb-6"
                style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
              >
                AI Features Are Shipping Faster Than Security Can Validate Them.
              </h2>
              <p className="text-arq-muted text-[17px] leading-[1.75]">
                Chatbots, copilots, RAG search, AI agents, and AI workflow automation are moving
                into production across SaaS, healthcare, fintech, legal, logistics, HR, insurance,
                and other regulated environments. The risk rarely starts with the model alone. It
                starts when AI connects to customer data, users, permissions, documents, workflows,
                logs, and third-party systems.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {riskCards.map((c) => (
                <div key={c.title} className="arq-card p-7">
                  <h3 className="font-display text-[18px] font-semibold text-arq-text mb-3">
                    {c.title}
                  </h3>
                  <p className="text-arq-muted text-[14.5px] leading-[1.7]">{c.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-14 max-w-[820px]">
              <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary mb-4">
                The questions buyers and security reviewers are asking
              </div>
              <ul className="space-y-3">
                {[
                  "Can one customer access another customer's data?",
                  "Can a prompt manipulate an internal workflow?",
                  "Can RAG expose restricted documents?",
                  "Are sensitive prompts and responses being logged?",
                  "Can the team prove the feature is safe enough for enterprise review?",
                ].map((q) => (
                  <li
                    key={q}
                    className="flex items-start gap-3 text-[15.5px] text-arq-text/90 leading-[1.6]"
                  >
                    <span className="text-arq-blue mt-1">›</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* OFFER */}
        <section className="bg-arq-bg py-20 md:py-[120px] px-6 border-t border-arq-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid md:grid-cols-[1fr_1fr] gap-12 items-start">
              <div>
                <div className="arq-label mb-5">The Offer</div>
                <h2
                  className="font-display font-semibold text-arq-text mb-6"
                  style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
                >
                  A Focused Review Before Your AI Feature Reaches Customers.
                </h2>
                <p className="text-arq-muted text-[17px] leading-[1.75] mb-6">
                  The AI Feature Security Review is a focused assessment for teams launching or
                  operating AI-enabled features. ArqeraAI reviews the application-layer risks
                  around data access, authorization, prompts, retrieval, logging, privacy,
                  third-party AI dependencies, and compliance evidence, then provides a
                  prioritized remediation path.
                </p>
                <p className="text-arq-text text-[15px] italic">
                  Assess. Remediate. Validate.
                </p>
              </div>
              <div className="arq-card p-8">
                <div className="font-mono-tech text-[11px] tracking-[0.1em] uppercase text-arq-tertiary mb-4">
                  Best For
                </div>
                <ul className="space-y-3">
                  {bestFor.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[15px] text-arq-text/90 leading-[1.55]">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0 text-arq-teal" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* REVIEW SCOPE */}
        <section id="scope" className="bg-arq-bg py-20 md:py-[120px] px-6 border-t border-arq-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center max-w-[820px] mx-auto mb-12">
              <div className="arq-label mb-5">Review Scope</div>
              <h2
                className="font-display font-semibold text-arq-text"
                style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
              >
                What We Review
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-x-10 gap-y-3 max-w-[1000px] mx-auto">
              {scopeItems.map((it) => (
                <div
                  key={it}
                  className="flex items-start gap-3 p-4 rounded-md bg-arq-surface1/40 border border-arq-border"
                >
                  <Check className="w-4 h-4 mt-1 flex-shrink-0 text-arq-teal" />
                  <span className="text-[15px] text-arq-text/90">{it}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DELIVERABLES */}
        <section className="bg-arq-bg py-20 md:py-[120px] px-6 border-t border-arq-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="max-w-[820px] mb-12">
              <div className="arq-label mb-5">Deliverables</div>
              <h2
                className="font-display font-semibold text-arq-text"
                style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
              >
                What You Get in the Review
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {deliverables.map((d, i) => (
                <div key={d.title} className="arq-card p-7">
                  <div className="font-mono-tech text-[12px] text-arq-blue mb-3 tracking-[0.08em]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-[20px] font-semibold text-arq-text mb-3">
                    {d.title}
                  </h3>
                  <p className="text-arq-muted text-[15px] leading-[1.7]">{d.body}</p>
                </div>
              ))}
            </div>
            <p className="text-arq-text text-[15px] italic text-center mt-10">
              Every finding comes with a fix, not just a flag.
            </p>

            <div className="mt-14 max-w-[860px] mx-auto">
              <div className="arq-card p-8 border-arq-blue/30">
                <div className="flex items-center justify-between mb-5">
                  <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-blue">
                    Example Finding
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.08em] px-2 py-1 rounded text-arq-tertiary border border-arq-border">
                    EXAMPLE ONLY
                  </span>
                </div>
                <h3 className="font-display text-[20px] font-semibold text-arq-text mb-5">
                  What a finding looks like in your report
                </h3>
                <dl className="space-y-3 text-[14.5px] leading-[1.65]">
                  {[
                    ["Severity", "Critical"],
                    ["Finding", "Cross-tenant data exposure through AI retrieval path"],
                    ["Risk", "A user may retrieve or summarize records outside their authorized tenant boundary"],
                    ["Impact", "Customer data exposure, enterprise security review failure, compliance escalation"],
                    ["Remediation", "Enforce tenant-aware retrieval filters, authorization checks, and validation tests before launch"],
                    ["Status", "Example only, not a real client finding"],
                  ].map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[140px_1fr] gap-4">
                      <dt className="font-mono-tech text-[11px] uppercase tracking-[0.08em] text-arq-tertiary pt-0.5">
                        {k}
                      </dt>
                      <dd className="text-arq-text/90">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="bg-arq-bg py-20 md:py-[120px] px-6 border-t border-arq-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="max-w-[820px] mb-12">
              <div className="arq-label mb-5">How It Works</div>
              <h2
                className="font-display font-semibold text-arq-text"
                style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
              >
                From Review to Remediation
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((s) => (
                <div key={s.n} className="arq-card p-7 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-md bg-arq-blue/10 border border-arq-blue/30 flex items-center justify-center">
                      <s.icon className="w-5 h-5 text-arq-blue" />
                    </div>
                    <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary">
                      {s.n}
                    </div>
                  </div>
                  <h3 className="font-display text-[18px] font-semibold text-arq-text mb-2">
                    {s.title}
                  </h3>
                  <p className="text-arq-muted text-[14px] leading-[1.7]">{s.body}</p>
                </div>
              ))}
            </div>
            <p className="text-arq-text text-[15px] italic text-center mt-10 max-w-[720px] mx-auto">
              We do not write reports and walk away. We fix problems and prove they are fixed.
            </p>
            <div className="text-center mt-6">
              <Link
                to="/methodology"
                className="text-arq-teal text-[14px] underline underline-offset-4 hover:text-arq-blue transition-colors"
              >
                See the full ArqeraAI methodology →
              </Link>
            </div>
          </div>
        </section>

        {/* ENGAGEMENT FIT */}
        <section className="bg-arq-bg py-20 md:py-[120px] px-6 border-t border-arq-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="max-w-[820px] mb-12">
              <div className="arq-label mb-5">Best Fit</div>
              <h2
                className="font-display font-semibold text-arq-text mb-5"
                style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
              >
                Built for Teams That Need to Ship Without Guessing.
              </h2>
              <p className="text-arq-muted text-[17px] leading-[1.75]">
                This review is designed for teams that need practical security assurance without
                slowing product momentum.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="arq-card p-8 border-arq-teal/30">
                <h3 className="font-display text-[20px] font-semibold text-arq-text mb-5">
                  Good Fit
                </h3>
                <ul className="space-y-3">
                  {goodFit.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[15px] text-arq-text/90 leading-[1.55]">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0 text-arq-teal" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="arq-card p-8">
                <h3 className="font-display text-[20px] font-semibold text-arq-text mb-5">
                  Not Ideal
                </h3>
                <ul className="space-y-3">
                  {notIdeal.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[15px] text-arq-muted leading-[1.55]">
                      <X className="w-4 h-4 mt-1 flex-shrink-0 text-arq-tertiary" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* EXPANSION PATH */}
        <section className="bg-arq-bg py-20 md:py-[120px] px-6 border-t border-arq-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="max-w-[820px] mb-12">
              <div className="arq-label mb-5">After the Review</div>
              <h2
                className="font-display font-semibold text-arq-text mb-5"
                style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
              >
                Assessment Is the Starting Point. Remediation Is Where Risk Closes.
              </h2>
              <p className="text-arq-muted text-[17px] leading-[1.75]">
                The review identifies and prioritizes the risk. After that, ArqeraAI can support
                remediation, validation, evidence preparation, and ongoing assurance so findings
                do not sit unresolved.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {expansion.map((e) => (
                <div key={e.title} className="arq-card p-7">
                  <h3 className="font-display text-[18px] font-semibold text-arq-text mb-3">
                    {e.title}
                  </h3>
                  <p className="text-arq-muted text-[14.5px] leading-[1.7]">{e.body}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/#services"
                className="text-arq-teal text-[14px] underline underline-offset-4 hover:text-arq-blue transition-colors"
              >
                See the full service lineup →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="book" className="bg-arq-bg py-20 md:py-[120px] px-6 border-t border-arq-border">
          <div className="max-w-[900px] mx-auto">
            <div className="arq-card p-10 md:p-14 text-center">
              <h2
                className="font-display font-semibold text-arq-text mb-5"
                style={{ fontSize: "clamp(26px, 3.4vw, 36px)", lineHeight: 1.25 }}
              >
                Ready to Review an AI Feature Before It Reaches Customers?
              </h2>
              <p className="text-arq-muted text-[16px] leading-[1.75] mb-8 max-w-[680px] mx-auto">
                In 45 minutes, we will understand the feature, data exposure, users, permissions,
                compliance obligations, and rollout timeline, then tell you exactly what a review
                would cover. No commitment. Written proposal within 48 hours if there is a fit.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => openBookingModal()}
                  className="bg-arq-blue text-white font-semibold text-[15px] px-8 py-4 rounded-md hover:bg-arq-blue-bright hover:shadow-[0_0_24px_rgba(26,111,212,0.35)] transition-all duration-150 tracking-[0.01em]"
                >
                  Book an AI Feature Security Review
                </button>
                <a
                  href="mailto:hello@arqeraai.com"
                  className="text-arq-teal text-[15px] font-medium hover:text-arq-blue underline underline-offset-4 transition-colors px-2 py-4"
                >
                  Contact ArqeraAI →
                </a>
              </div>
              <p className="text-arq-tertiary text-[13px] italic mt-8">
                Secure the feature. Prove the fix. Ship with confidence.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-arq-bg py-20 md:py-[120px] px-6 border-t border-arq-border">
          <div className="max-w-[900px] mx-auto">
            <div className="text-center mb-12">
              <div className="arq-label mb-5">FAQ</div>
              <h2
                className="font-display font-semibold text-arq-text"
                style={{ fontSize: "clamp(26px, 3.4vw, 36px)", lineHeight: 1.25 }}
              >
                Questions teams ask before booking.
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((f) => (
                <details
                  key={f.q}
                  className="arq-card p-6 group [&[open]>summary>span:last-child]:rotate-45"
                >
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                    <span className="font-display text-[17px] font-semibold text-arq-text">
                      {f.q}
                    </span>
                    <span className="text-arq-blue text-[22px] leading-none transition-transform duration-200">
                      +
                    </span>
                  </summary>
                  <p className="text-arq-muted text-[15px] leading-[1.75] mt-4">{f.a}</p>
                </details>
              ))}
            </div>
            <p className="text-arq-tertiary text-[13px] text-center mt-10 italic">
              Practitioner credibility. Repeatable methodology. Audit-ready evidence.
            </p>
          </div>
        </section>
      </main>

      <ArqFooter />
    </div>
  );
}
